import React, { useEffect, useRef, useState } from 'react'
import { Stack } from '@mui/material'
import { Timestamp } from '@weblite/ts-action/types'
import { closeCurrentModal } from 'weblite-web-modal-viewer'
import { EmbeddedLink, RawImageMessage } from '@weblite/ts-action/message'
import { t } from '$i18n'
import { toLocale } from '$func/text'
import { MediaCard } from '$common/media-card'
import { useContentFileStatus } from '$common/react-hooks/message'
import { ActionHandler } from '$common/action-handler'
import { MediaOwnerListItem } from '$common/media-owner-list-item/media-owner-list-item'
import { MediaViewerCaption } from '$common/media-viewer-caption/media-viewer-caption'
import { AppBarBackTitleActionMenu } from '$common/app-bar/app-bar-back-title-action-menu'
import { ViewerTransformWrapper } from '$comp/modals/media-viewer/viewer-transform-wrapper'

export interface ImageDetails {
  src?: string
  description?: string
  owner?: {
    id: string
    type: 'USER' | 'WAPP' | 'CHANNEL'
  }
  date?: Timestamp
  functions?: {
    onDownload?: () => unknown
    onShare?: () => unknown
    onRemove?: () => unknown
    onForward?: () => unknown
    onShowSharedMedia?: () => unknown
    onEdit?: () => unknown
  }
  attachment?: RawImageMessage['attachment']
  links?: EmbeddedLink[]
}

interface Props {
  media: ImageDetails
  total?: number
  current?: number
  onPrev?: () => void
  onNext?: () => void
  loading?: boolean
}

export function ImageViewerModal({
  media,
  total,
  current,
  onPrev,
  onNext,
  loading,
}: Props) {
  const { src, description, owner, date, functions, attachment, links } =
    media || {}
  const {
    onShare,
    onShowSharedMedia,
    onDownload,
    onForward,
    onRemove,
    onEdit,
  } = functions || {}
  const { base64: thumbnail, fileId, width, height } = attachment || {}
  const {
    state,
    percentage,
    blobURL,
    cancelOperation,
    startDownload,
  } = useContentFileStatus({ fileId, uploadId: null, messageType: 'IMAGE' })
  const source = src || blobURL || thumbnail

  useEffect(() => {
    if (src || blobURL) return
    startDownload()
  }, [src, blobURL, startDownload])

  const cardRef = useRef<HTMLDivElement>(null)
  const captionRef = useRef<HTMLDivElement>(null)
  const appbarRef = useRef<HTMLDivElement>(null)
  const imageContainerRef = useRef<HTMLDivElement>(null)

  const [marginRight, setMarginRight] = useState(0)
  const [showDetails, setShowDetails] = useState(true)
  const [isVisible, setIsVisible] = useState(false)

  const revealDetails = () => {
    captionRef.current.style.visibility = 'visible'
    appbarRef.current.style.visibility = 'visible'
    setShowDetails(true)
  }

  const concealDetails = () => {
    if (captionRef.current) captionRef.current.style.visibility = 'hidden'
    if (appbarRef.current) appbarRef.current.style.visibility = 'hidden'
    setShowDetails(false)
  }

  useEffect(() => {
    if (height >= width) {
      const offset =
        cardRef.current.clientWidth - imageContainerRef.current.clientWidth
      setMarginRight(offset / 2)
    }
    setIsVisible(true)
  }, [])

  return (
    <MediaCard
      onClick={() => {
        if (showDetails) concealDetails()
        else revealDetails()
      }}
      appbarRef={appbarRef}
      appbarComponent={
        <AppBarBackTitleActionMenu
          title={
            current && total && current > 0
              ? `${toLocale(current)} ${t('common.from')} ${toLocale(total)}`
              : t('message.IMAGE')
          }
          onBack={closeCurrentModal}
          functions={{
            onRemove,
            onDownload,
            onForward,
            onShowSharedMedia,
            onEdit,
          }}
          loading={loading}
        />
      }
      cardRef={cardRef}
    >
      <Stack
        ref={imageContainerRef}
        sx={{
          position: 'absolute',
          marginTop: 0,
          marginRight: `${marginRight}px`,
          visibility: isVisible ? 'visible' : 'hidden',
          width: '100%',
          height: '100%',
        }}
      >
        {state !== 'AVAILABLE' && !src && (
          <ActionHandler
            size={50}
            state={state}
            percentage={percentage}
            onCancelClick={cancelOperation}
            onDownloadClick={startDownload}
            sx={{
              backgroundColor: '#505962B2',
            }}
            rootSx={{
              position: 'absolute',
              left: 'calc(50% - 25px)',
              top: 'calc(50% - 25px)',
              zIndex: 1,
            }}
          />
        )}
        <ViewerTransformWrapper onPrev={onPrev} onNext={onNext}>
          <img
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            src={source}
            alt="test"
          />
        </ViewerTransformWrapper>
      </Stack>
      <Stack
        ref={captionRef}
        sx={{
          zIndex: 2,
          position: 'absolute',
          bottom: 0,
          backgroundColor: '#00000080',
          width: '100%',
        }}
        onClick={e => e.stopPropagation()}
      >
        {description && (
          <MediaViewerCaption description={description} links={links} />
        )}
        {showDetails && owner && (
          <MediaOwnerListItem owner={owner} date={date} onShare={onShare} />
        )}
      </Stack>
    </MediaCard>
  )
}

ImageViewerModal.defaultProps = {
  total: null,
  current: null,
  onPrev: null,
  onNext: null,
  loading: false,
}
